import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import { Coffee, Lock, Discount } from '@mui/icons-material';

const BuyMeCoffeePaywall = ({ onVerify, buyMeCoffeeLink }) => {
  // ─────────────────────────────────────────────────────────────
  // 🔑  Local state
  // ─────────────────────────────────────────────────────────────
  const [showDialog, setShowDialog]   = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [discountCode, setDiscountCode]         = useState('');   // ← NEW
  const [error, setError]             = useState('');
  const [showDiscount, setShowDiscount] = useState(false);

  // Simple demo "databases"
  const validVerificationCodes = ['THANKYOU2025!', 'COFFEE2025'];
  const validDiscountCodes     = ['WELCOME10', 'SUMMER25'];       // ← NEW
  const localDiscountCodes     = ['Carlos123IsCool'];             // ← Local codes that unlock premium directly

  // ─────────────────────────────────────────────────────────────
  // 🔑  Effects
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    // Add script to enable the Buy Me Coffee verification
    const script = document.createElement('script');
    script.src = 'https://cdnjs.buymeacoffee.com/1.0.0/button.prod.min.js';
    script.async = true;
    document.body.appendChild(script);
    
    // Listen for Buy Me Coffee success events
    window.addEventListener('buymeacoffee-widget-success', handleBuyMeCoffeeSuccess);
    
    return () => {
      window.removeEventListener('buymeacoffee-widget-success', handleBuyMeCoffeeSuccess);
      document.body.removeChild(script);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 🔑  Handlers
  // ─────────────────────────────────────────────────────────────
  const handleOpenDialog  = () => { setShowDialog(true);  setError(''); };
  const handleCloseDialog = () => { setShowDialog(false); setError(''); };

  const handleBuyMeCoffeeSuccess = (event) => {
    console.log('Buy Me Coffee payment successful!', event);
    localStorage.setItem('userPremiumStatus', 'true');
    onVerify(true);
    // Add 2-second delay then trigger PDF load
    setTimeout(() => {
      // Find and click the Load PDF button
      const loadPdfButton = document.querySelector('button[type="submit"]');
      if (loadPdfButton && loadPdfButton.textContent.includes('Load PDF')) {
        loadPdfButton.click();
      }
    }, 2000);
  };

  const handleVerify = () => {
    if (validVerificationCodes.includes(verificationCode.toUpperCase())) {
      localStorage.setItem('userPremiumStatus', 'true');
      onVerify(true);
      handleCloseDialog();
      // Add 2-second delay then trigger PDF load
      setTimeout(() => {
        // Find and click the Load PDF button
        const loadPdfButton = document.querySelector('button[type="submit"]');
        if (loadPdfButton && loadPdfButton.textContent.includes('Load PDF')) {
          loadPdfButton.click();
        }
      }, 2000);
    } else {
      setError(
        'Invalid verification code. Please make sure you entered the code you received after your donation.'
      );
    }
  };

  const handleDiscountCode = () => {
    // Check if it's a local discount code first
    if (localDiscountCodes.includes(discountCode)) {
      localStorage.setItem('userPremiumStatus', 'true');
      onVerify(true);
      // Add 2-second delay then trigger PDF load
      setTimeout(() => {
        // Find and click the Load PDF button
        const loadPdfButton = document.querySelector('button[type="submit"]');
        if (loadPdfButton && loadPdfButton.textContent.includes('Load PDF')) {
          loadPdfButton.click();
        }
      }, 2000);
    } else {
      // Use external discount link
      window.open(getDiscountLink(), '_blank');
    }
  };

  const getDiscountLink = () =>
    validDiscountCodes.includes(discountCode.toUpperCase())
      ? `${buyMeCoffeeLink}?discount=${discountCode.toUpperCase()}`
      : buyMeCoffeeLink;

  // ─────────────────────────────────────────────────────────────
  // 🔑  Render
  // ─────────────────────────────────────────────────────────────
  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Lock color="primary" sx={{ fontSize: { xs: 36, sm: 48 }, mb: 2 }} />
        <Typography
          variant="h5"
          gutterBottom
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
        >
          Unlock PDF Export
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, px: { xs: 1, sm: 3 } }}
        >
          Support the developer by buying a coffee to unlock the full CV export
          functionality.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<Coffee />}
            onClick={() => window.open(buyMeCoffeeLink, '_blank')}
            sx={{ borderRadius: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            size="medium"
          >
            Buy Me a Coffee
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenDialog}
            sx={{ borderRadius: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            size="medium"
          >
            I've Donated
          </Button>
        </Box>

        {!showDiscount && (
          <Link
            component="button"
            variant="body2"
            onClick={() => setShowDiscount(true)}
            sx={{ mb: 1 }}
          >
            Have a discount code?
          </Link>
        )}

        {showDiscount && (
          <Box sx={{ width: '100%', maxWidth: 400, mb: 2 }}>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Discount Code
              </Typography>
            </Divider>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter discount code"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
              />
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Discount />}
                onClick={handleDiscountCode}
                disabled={!discountCode}
              >
                Apply
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={showDialog} onClose={handleCloseDialog}>
        <DialogTitle>Verify Your Donation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please enter the verification code you received after your donation
            on Buy Me a Coffee.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Verification Code"
            fullWidth
            variant="outlined"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleVerify} variant="contained">
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BuyMeCoffeePaywall;
